import { useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { LayoutFive } from "@/layouts";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import HeroSectionStyleFive from "@/components/hero/styleFive";
import HeroContentSection from "@/components/hero/heroContentSection";
import AboutHomeSection from "@/components/aboutUs/aboutHomeSection";
import AboutUsStyleTwo from "@/components/aboutUs/aboutUsStyleTwo";
import ProductItem from "@/components/product";
import DirectorSection from "@/components/director";
import LatestBlogFeeds from "@/components/latestBlogFeeds";
import OurTestimonial from "@/components/ourTestimonial";
import OurGroupSection from "@/components/ourGroup";
import FaqSection from "@/components/faq";
import ProjectsOverviewSection from "@/components/projectsOverview";
import { getLatestBlogs } from "@/lib/blog";
import { getAllProjects } from "@/lib/projects";
import { HOME_SCHEMA } from "@/lib/homeSchema";

function HomePage({ latestBlogs = [], homepageProjects = [] }) {
  const { products } = useSelector((state) => state.product);
  const [activeProjectStage, setActiveProjectStage] = useState("ongoing");
  const availableProjects = homepageProjects.length ? homepageProjects : products;
  const projectTabs = [
    { value: "ongoing", label: "Ongoing" },
    { value: "upcoming", label: "Upcoming" },
    { value: "completed", label: "Completed" },
  ];
  const projectCounts = useMemo(
    () =>
      availableProjects.reduce(
        (counts, project) => {
          const stage = project.project_stage || "ongoing";
          if (Object.prototype.hasOwnProperty.call(counts, stage)) counts[stage] += 1;
          return counts;
        },
        { ongoing: 0, upcoming: 0, completed: 0 }
      ),
    [availableProjects]
  );
  const visibleProjects = useMemo(
    () =>
      availableProjects.filter(
        (project) => (project.project_stage || "ongoing") === activeProjectStage
      ),
    [activeProjectStage, availableProjects]
  );

  const { cartItems } = useSelector((state) => state.cart);
  const { wishlistItems } = useSelector((state) => state.wishlist);
  const { compareItems } = useSelector((state) => state.compare);

  return (
    <>
      <Head>
        {/*
          The LCP element is the hero slide div whose `background-image`
          is /img/slider/41.webp. Browsers don't discover background-images
          in CSS until the stylesheet loads, so we preload the poster here
          in <head> to start the fetch during HTML parsing.
        */}
        <link
          rel="preload"
          as="image"
          href="/img/slider/41.webp"
          type="image/webp"
          fetchpriority="high"
        />
      </Head>
      <PageViewBeacon contentType="home" path="/" />
      <SeoHead
        path="/"
        fallbackTitle="Real Estate Developer in Nagpur | Siddhivinayak Group"
        fallbackDescription="Trusted real estate developer in Nagpur. Premium 2 & 3 BHK flats in Ram Nagar, Hanuman Nagar, Narsala, townships & RERA-approved RL plots in Bahadura. Kunal Padole Group."
        seo={{
          seo_title: "Real Estate Developer in Nagpur | Siddhivinayak Group",
          meta_description:
            "Trusted real estate developer in Nagpur. 2 & 3 BHK flats in Ram Nagar, Hanuman Nagar, Narsala, RERA-approved RL plots in Bahadura. Kunal Padole Group.",
          focus_keyword: "real estate developer in Nagpur",
          secondary_keywords: [
            "best real estate developer in Nagpur",
            "premium residential projects in Nagpur",
            "RL plots in Nagpur",
            "2 BHK 3 BHK flats in Nagpur",
            "property investment in Nagpur",
            "Kunal Padole Group real estate Nagpur",
            "flats in Ram Nagar Hanuman Nagar Narsala Bahadura Ambazari",
          ],
          canonical_url: "",
          schema_json: HOME_SCHEMA,
          og_image: "/img/og/siddhivinayak-group-og.jpg",
          og_title:
            "Real Estate Developer in Nagpur | Siddhivinayak Group",
          og_description:
            "Premium 2 & 3 BHK flats, townships, and RERA-approved RL plots across Nagpur. Kunal Padole Group.",
        }}
        publisherName="Siddhivinayak Group"
        speakable_selectors={[
          ".faq-question",
          ".about-us-info-wrap h2",
          ".section-title",
        ]}
      />
      {/* Hidden but crawler-visible SEO block. Off-screen via the existing
          `.screen-reader-text` utility in _global.scss. */}
      <LayoutFive topbar={false}>
        <HeroSectionStyleFive />
        <HeroContentSection sectionSpace="pt-30 pb-30" />

        <AboutHomeSection sectionSpace="pt-50 pb-90" />

        <DirectorSection sectionSpace="pt-60 pb-90" />
        <div className="section-bg-1">
          <ProjectsOverviewSection sectionSpace="pt-90 pb-90" />
        </div>


        <section className="ltn__product-area home-projects-area pt-90 pb-90" aria-labelledby="home-projects-title">
          <Container>
            <div className="section-title-area text-center">
              <h2 className="section-title" id="home-projects-title">Our Projects</h2>
            </div>
            <div className="projects-stage-tabs" role="tablist" aria-label="Filter homepage projects by status">
              {projectTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeProjectStage === tab.value}
                  className={activeProjectStage === tab.value ? "is-active" : ""}
                  onClick={() => setActiveProjectStage(tab.value)}
                >
                  <span>{tab.label}</span>
                  <span className="projects-stage-count" aria-label={`${projectCounts[tab.value]} projects`}>
                    {projectCounts[tab.value]}
                  </span>
                </button>
              ))}
            </div>
            <Row>
              {visibleProjects.map((product, index) => (
                <Col key={product.id || index} xs={12} sm={6} lg={4}>
                  <ProductItem
                    productData={product}
                    slug={product.slug || product.id}
                    baseUrl="projects"
                    cartItem={cartItems.find((item) => item.id === product.id)}
                    wishlistItem={wishlistItems.find((item) => item.id === product.id)}
                    compareItem={compareItems.find((item) => item.id === product.id)}
                    variant="featured"
                  />
                </Col>
              ))}
              {!visibleProjects.length ? (
                <Col xs={12}>
                  <p className="text-center py-4 mb-0">No {activeProjectStage} projects available.</p>
                </Col>
              ) : null}
            </Row>
          </Container>
        </section>

        <div className="section-bg-1">
          <AboutUsStyleTwo sectionSpace="pt-90 pb-90" />
        </div>

        <OurTestimonial sectionSpace="pt-70 pb-40" bgImage="" />
        <div className="section-bg-1">
          <FaqSection sectionSpace="pt-40 pb-90" />
        </div>

        <LatestBlogFeeds
          sectionSpace="pt-90 pb-90"
          subTitle="Blog"
          title="Latest From Siddhivinayak Group"
          data={latestBlogs}
        />

        <div className="section-bg-1">
          <OurGroupSection sectionSpace="pt-90 pb-90" />
        </div>
        {/* Hidden but crawler-visible SEO block. Placed at the end of the
            visible content so DOM order is H1 → H2/H3 (no heading levels
            precede the page H1). */}
        <div className="screen-reader-text">
          <h2>About Siddhivinayak Group — Real Estate Developer in Nagpur</h2>
          <p>
            Siddhivinayak Group is a trusted real estate developer in Nagpur,
            recognized for premium 2 BHK and 3 BHK flats, luxury residential
            projects, plotted developments, and RERA-approved RL plots. As a
            venture of the Kunal Padole Group, we deliver quality construction,
            transparent dealings, and long-term investment value across Central
            India.
          </p>
          <h3>Areas We Serve in Nagpur</h3>
          <ul>
            <li>
              <Link href="/projects?area=ram-nagar">
                2 &amp; 3 BHK flats in Ram Nagar
              </Link>
            </li>
            <li>
              <Link href="/projects?area=hanuman-nagar">
                Flats in Hanuman Nagar, near Krida Square
              </Link>
            </li>
            <li>
              <Link href="/projects?area=narsala">
                Ayodhya City township in Narsala
              </Link>
            </li>
            <li>
              <Link href="/projects?area=bahadura">
                RL plots in Bahadura (Siddhivinayak Nagari)
              </Link>
            </li>
            <li>
              <Link href="/projects?area=ambazari">
                Apartments near Ambazari Lake
              </Link>
            </li>
          </ul>
        </div>
      </LayoutFive>
    </>
  );
}

export async function getStaticProps() {
  let latestBlogs = [];
  let homepageProjects = [];
  [latestBlogs, homepageProjects] = await Promise.all([
    getLatestBlogs(6).catch(() => []),
    getAllProjects().catch(() => []),
  ]);
  // Keep homepage SSG payload lean: cards and filters do not need each
  // project's complete SEO/schema/facts payload.
  homepageProjects = homepageProjects.map((project) => ({
    id: project.id,
    title: project.title,
    slug: project.slug,
    location: project.location || "",
    excerpt: project.excerpt || "",
    card_blurb: project.card_blurb || "",
    project_stage: project.project_stage || "ongoing",
    project_status: project.project_status || "Ongoing",
    productImg: project.productImg || project.featured_image || "",
    featured_image: project.featured_image || project.productImg || "",
    description: project.description || {
      shortDescription: project.excerpt || "",
      fullDescription: project.excerpt || "",
    },
  }));
  return {
    props: { latestBlogs, homepageProjects },
    revalidate: 600,
  };
}

export default HomePage;
